import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@roger/ui-components';
import { Clipboard, Loader2, SendHorizonal } from 'lucide-react';
import type {
  BespokeTemplateCreate,
  BespokeTemplateResponse,
  BespokeTemplateUpdate,
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  VersionedQuestionSchema,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { VisitTypeId } from '../../services/api/models/VisitTypeId';
import { client } from '../../services/api';
import {
  getCurrentFormSchemaVersionsRogerdocFormSchemasCurrentGet,
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
} from '../../services/api/sdk.gen';
import { useToast } from '../../hooks/useToastContext';
import type { Question, FormValue } from '../../components/oasis/types';
import { BespokeLayoutRenderer } from '../../components/oasis/bespoke/layout/BespokeLayoutRenderer';
import { tryParseLayoutJson } from '../../components/oasis/bespoke/layout/tryParseLayoutJson';
import { collectLayoutReferencedQids } from '../../components/oasis/bespoke/layout/collectLayoutReferencedQids';
import { collectSectionCoverageQids } from '../../components/oasis/bespoke/verification/bespokeCoverageSearch';
import type { CoverageSearchResult } from '../../components/oasis/bespoke/verification/bespokeCoverageSearch';
import { checkLayoutJsonReachability } from '../../components/oasis/bespoke/verification/checkLayoutJsonReachability';
import { composeVibecodeLlmBundle } from '../../components/oasis/bespoke/layout-studio/composeVibecodeLlmBundle';
import { formTreeSectionToQuestions } from '../../components/oasis/bespoke/layout-studio/formTreeToQuestions';
import {
  type FormSchemaStudioPick,
  makeFormSchemaVersionPickId,
} from './schemaStudioPick';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';

/** Section bucket shape from `schema_data` (RogerDoc question tree). */
type StudioFormTree = Record<string, unknown>;

type VibecoderChatTurn = { role: 'user' | 'assistant'; content: string };

/** Shown in the composer when there is no chat history yet (user can edit before sending). */
const DEFAULT_LAYOUT_ASSISTANT_PROMPT =
  'Create a layout for the selected form and section';

/** Strip fenced JSON models sometimes paste into assistant_message; layout lives in layout_json only. */
function sanitizeVibecoderAssistantDisplay(text: string): string {
  return text
    .replace(/```(?:json)?\s*[\s\S]*?```/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export interface TemplateVibecoderProps {
  editingTemplate: BespokeTemplateResponse | null;
  onDiscardEdit: () => void;
  onSaveJson: (
    data: BespokeTemplateCreate | BespokeTemplateUpdate
  ) => void | Promise<void>;
  saving: boolean;
  /** Shared catalog from Form Schema Manager (single HTTP fetch upstream). */
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  catalogLoading: boolean;
}

function emptyDraftState() {
  return {
    schemaPickId: '',
    sectionName: '',
    layoutJsonText: '',
    name: '',
    description: '',
    isEnabled: true,
  };
}

export function TemplateVibecoder({
  editingTemplate: initial,
  onDiscardEdit,
  onSaveJson,
  saving,
  formTypeCatalog,
  catalogLoading,
}: TemplateVibecoderProps) {
  const toast = useToast();

  const [studioFormType, setStudioFormType] = useState<RogerDocFormType | null>(
    null
  );
  const [studioVersions, setStudioVersions] = useState<FormSchemaVersion[]>([]);
  const [studioVersionsLoading, setStudioVersionsLoading] = useState(false);

  const isEditingPublishedJson = !!initial?.id && !!initial.layout_json;

  useEffect(() => {
    if (formTypeCatalog.length === 0) return;
    if (initial?.layout_json) return;
    setStudioFormType(prev => {
      if (prev != null && formTypeCatalog.some(r => r.form_type === prev)) {
        return prev;
      }
      return formTypeCatalog[0].form_type;
    });
  }, [formTypeCatalog, initial?.layout_json]);

  useEffect(() => {
    const t = initial;
    if (!t?.layout_json || formTypeCatalog.length === 0) return;
    let cancelled = false;
    void (async () => {
      for (const row of formTypeCatalog) {
        if (cancelled) return;
        try {
          const res =
            await getCurrentFormSchemaVersionsRogerdocFormSchemasCurrentGet({
              query: { visit_type: row.canonical_visit_type_id },
              client,
            });
          const vs = (res?.data as FormSchemaVersion[]) ?? [];
          if (vs.some(v => v.name === t.schema_version_name)) {
            if (!cancelled) setStudioFormType(row.form_type);
            return;
          }
        } catch {
          continue;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    initial?.id,
    initial?.schema_version_name,
    initial?.layout_json,
    formTypeCatalog,
  ]);

  useEffect(() => {
    const entry = formTypeCatalog.find(r => r.form_type === studioFormType);
    const vt = entry?.canonical_visit_type_id;
    if (!vt) {
      setStudioVersions([]);
      setStudioVersionsLoading(false);
      return;
    }
    let cancelled = false;
    setStudioVersionsLoading(true);
    void (async () => {
      try {
        const res =
          await getCurrentFormSchemaVersionsRogerdocFormSchemasCurrentGet({
            query: { visit_type: vt },
            client,
          });
        if (!cancelled) {
          setStudioVersions((res?.data as FormSchemaVersion[]) ?? []);
        }
      } catch {
        if (!cancelled) setStudioVersions([]);
      } finally {
        if (!cancelled) setStudioVersionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studioFormType, formTypeCatalog]);

  const [schemaPickId, setSchemaPickId] = useState('');

  useEffect(() => {
    setSchemaPickId('');
  }, [studioFormType]);

  const [sectionName, setSectionName] = useState('');
  const [layoutJsonText, setLayoutJsonText] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  const [chatMessages, setChatMessages] = useState<VibecoderChatTurn[]>([]);
  const [chatInput, setChatInput] = useState(DEFAULT_LAYOUT_ASSISTANT_PROMPT);
  const [chatSending, setChatSending] = useState(false);

  const prevEditingIdRef = useRef<string | null>(null);

  useEffect(() => {
    setChatMessages([]);
    setChatInput(DEFAULT_LAYOUT_ASSISTANT_PROMPT);
  }, [initial?.id]);

  useEffect(() => {
    const t = initial;
    if (t?.layout_json) {
      setSectionName(t.section_name);
      setLayoutJsonText(JSON.stringify(t.layout_json, null, 2));
      setName(t.name ?? '');
      setDescription(t.description ?? '');
      setIsEnabled(t.is_enabled);
      prevEditingIdRef.current = t.id;
      return;
    }

    if (prevEditingIdRef.current !== null && !t) {
      const d = emptyDraftState();
      setSchemaPickId(d.schemaPickId);
      setSectionName(d.sectionName);
      setLayoutJsonText(d.layoutJsonText);
      setName(d.name);
      setDescription(d.description);
      setIsEnabled(d.isEnabled);
      prevEditingIdRef.current = null;
    }
  }, [initial]);

  const [sectionQuestions, setSectionQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [studioAnswers, setStudioAnswers] = useState<Record<string, FormValue>>(
    {}
  );
  const [coverage, setCoverage] = useState<CoverageSearchResult | null>(null);
  const [coverageWorking, setCoverageWorking] = useState(false);

  const coverageCycleRef = useRef(0);
  const schemaSectionFetchGen = useRef(0);
  const [schemaSectionKeys, setSchemaSectionKeys] = useState<string[]>([]);
  const [schemaSectionsLoading, setSchemaSectionsLoading] = useState(false);

  const studioCatalogEntry = useMemo(
    () => formTypeCatalog.find(r => r.form_type === studioFormType) ?? null,
    [formTypeCatalog, studioFormType]
  );

  const canonicalVisitForStudio: VisitTypeId | null =
    studioCatalogEntry?.canonical_visit_type_id ?? null;

  const studioSchemaPicks = useMemo((): FormSchemaStudioPick[] => {
    if (
      canonicalVisitForStudio == null ||
      studioFormType == null ||
      studioCatalogEntry == null
    ) {
      return [];
    }
    const displayLabel = studioCatalogEntry.display_label;
    const picks = studioVersions.map(v => ({
      id: makeFormSchemaVersionPickId(v),
      rogerDocFormType: studioFormType,
      visitType: canonicalVisitForStudio,
      formTypeDisplayLabel: displayLabel,
      label: [
        v.name,
        ...(v.version != null && `${v.version}`.length > 0
          ? [`v${v.version}`]
          : []),
      ].join(' · '),
      schemaVersion: v,
    }));
    return picks.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
  }, [
    studioVersions,
    studioFormType,
    canonicalVisitForStudio,
    studioCatalogEntry,
  ]);

  const resolveStudioSectionQuestions = useCallback(
    async (
      pickId: string,
      sectionKey: string,
      _fallbackSchemaVersionName?: string | null
    ): Promise<Question[]> => {
      const trimmed = pickId.trim();
      const pick = studioSchemaPicks.find(p => p.id === trimmed);
      if (!pick) return [];

      try {
        const fv = pick.schemaVersion;
        const res =
          await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
            query: { visit_type: pick.visitType },
            body: {
              schema_version: {
                name: fv.name,
                version: fv.version ?? null,
              },
            },
            client,
          });
        const payload = res?.data as VersionedQuestionSchema | undefined;
        const tree = payload?.schema_data as StudioFormTree | null | undefined;
        const rows = tree?.[sectionKey];
        if (!Array.isArray(rows)) return [];
        return formTreeSectionToQuestions(rows);
      } catch {
        return [];
      }
    },
    [studioSchemaPicks]
  );

  const resolveStudioSchemaSectionKeys = useCallback(
    async (pickId: string): Promise<string[]> => {
      const pick = studioSchemaPicks.find(p => p.id === pickId.trim());
      if (!pick) return [];

      try {
        const fv = pick.schemaVersion;
        const res =
          await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
            query: { visit_type: pick.visitType },
            body: {
              schema_version: {
                name: fv.name,
                version: fv.version ?? null,
              },
            },
            client,
          });
        const payload = res?.data as VersionedQuestionSchema | undefined;
        const tree = payload?.schema_data as StudioFormTree | null | undefined;
        if (!tree || typeof tree !== 'object') return [];
        return Object.keys(tree).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
      } catch {
        return [];
      }
    },
    [studioSchemaPicks]
  );

  useEffect(() => {
    const t = initial;
    if (!t?.layout_json) return;
    const pid =
      studioSchemaPicks.find(
        p => p.schemaVersion.name === t.schema_version_name
      )?.id ?? '';
    setSchemaPickId(pid);
  }, [initial, studioSchemaPicks]);

  const activePick = useMemo((): FormSchemaStudioPick | undefined => {
    if (schemaPickId) {
      return studioSchemaPicks.find(p => p.id === schemaPickId);
    }
    if (initial?.layout_json) {
      return studioSchemaPicks.find(
        p => p.schemaVersion.name === initial.schema_version_name
      );
    }
    return undefined;
  }, [schemaPickId, studioSchemaPicks, initial]);

  useEffect(() => {
    if (isEditingPublishedJson) return;

    const pickIdTrim = schemaPickId.trim();
    if (!pickIdTrim) {
      schemaSectionFetchGen.current += 1;
      setSchemaSectionKeys([]);
      setSchemaSectionsLoading(false);
      return;
    }

    schemaSectionFetchGen.current += 1;
    const requestGen = schemaSectionFetchGen.current;

    let cancelled = false;
    setSchemaSectionsLoading(true);
    setSchemaSectionKeys([]);

    const tid = window.setTimeout(() => {
      void resolveStudioSchemaSectionKeys(pickIdTrim)
        .then(keys => {
          if (cancelled || requestGen !== schemaSectionFetchGen.current) {
            return;
          }
          setSchemaSectionKeys(keys);
          setSectionName(prev => (prev && keys.includes(prev) ? prev : ''));
          setSchemaSectionsLoading(false);
        })
        .catch(() => {
          if (cancelled || requestGen !== schemaSectionFetchGen.current) {
            return;
          }
          setSchemaSectionKeys([]);
          setSchemaSectionsLoading(false);
        });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(tid);
      if (requestGen === schemaSectionFetchGen.current) {
        setSchemaSectionsLoading(false);
      }
    };
  }, [schemaPickId, isEditingPublishedJson, resolveStudioSchemaSectionKeys]);

  useEffect(() => {
    let cancelled = false;
    async function loadQuestions() {
      const editJson = !!initial?.layout_json;
      const sec = editJson ? initial!.section_name : sectionName.trim();

      if (!sec.trim()) {
        setSectionQuestions([]);
        return;
      }

      const fallbackName = editJson ? initial!.schema_version_name : undefined;

      let effectivePickId = schemaPickId.trim();
      if (!effectivePickId && fallbackName) {
        effectivePickId =
          studioSchemaPicks.find(p => p.schemaVersion.name === fallbackName)
            ?.id ?? '';
      }

      if (!effectivePickId) {
        setSectionQuestions([]);
        return;
      }

      setQuestionsLoading(true);
      try {
        const qs = await resolveStudioSectionQuestions(
          effectivePickId,
          sec.trim(),
          fallbackName
        );
        if (!cancelled) {
          setSectionQuestions(qs);
        }
      } catch (e) {
        console.error('Failed to fetch section schema for vibecoder:', e);
        if (!cancelled) setSectionQuestions([]);
      } finally {
        if (!cancelled) setQuestionsLoading(false);
      }
    }

    void loadQuestions();
    return () => {
      cancelled = true;
    };
  }, [
    schemaPickId,
    sectionName,
    initial,
    studioSchemaPicks,
    resolveStudioSectionQuestions,
  ]);

  const parsedLayout = useMemo(
    () => tryParseLayoutJson(layoutJsonText),
    [layoutJsonText]
  );

  useEffect(() => {
    if (!parsedLayout.ok || !parsedLayout.layout) return;
    setStudioAnswers({});
  }, [
    parsedLayout.ok,
    parsedLayout.ok ? JSON.stringify(parsedLayout.layout) : '',
  ]);

  useEffect(() => {
    const cycle = ++coverageCycleRef.current;
    if (!parsedLayout.ok || sectionQuestions.length === 0) {
      setCoverage(null);
      setCoverageWorking(false);
      return;
    }

    const handle = window.setTimeout(() => {
      void (async () => {
        if (cycle !== coverageCycleRef.current) return;
        setCoverageWorking(true);
        try {
          const result = await checkLayoutJsonReachability(
            parsedLayout.layout,
            sectionQuestions
          );
          if (cycle !== coverageCycleRef.current) return;
          setCoverage(result);
        } finally {
          if (cycle === coverageCycleRef.current) {
            setCoverageWorking(false);
          }
        }
      })();
    }, 600);

    return () => {
      window.clearTimeout(handle);
    };
  }, [parsedLayout, sectionQuestions]);

  /** All QIDs in the loaded section subtree (top-level + nested `subitems`), same scope as coverage + `schemaMap`. */
  const schemaQidSet = useMemo(
    () => new Set(collectSectionCoverageQids(sectionQuestions)),
    [sectionQuestions]
  );

  const strayLayoutQids = useMemo(() => {
    if (!parsedLayout.ok) return [];
    return [...collectLayoutReferencedQids(parsedLayout.layout)].filter(
      qid => !schemaQidSet.has(qid)
    );
  }, [parsedLayout, schemaQidSet]);

  const targetN =
    sectionQuestions.length > 0
      ? collectSectionCoverageQids(sectionQuestions).length
      : 0;

  const reachableCount =
    coverage && targetN > 0 ? coverage.covered.length : null;

  const llmVisitSummary =
    activePick != null ? activePick.formTypeDisplayLabel : null;

  const buildContextBundleOrNull = useCallback((): string | null => {
    if (sectionQuestions.length === 0) return null;
    const verName = (
      initial?.schema_version_name?.trim() ||
      activePick?.schemaVersion.name.trim() ||
      ''
    ).trim();
    const sec = (
      sectionName.trim() ||
      initial?.section_name?.trim() ||
      ''
    ).trim();
    if (!verName || !sec || !activePick) return null;
    return composeVibecodeLlmBundle({
      visitTypeSummary: llmVisitSummary ?? verName,
      schemaVersionName: verName,
      sectionName: sec,
      questions: sectionQuestions,
    });
  }, [sectionQuestions, activePick, initial, sectionName, llmVisitSummary]);

  /** Allow assistant send when editing an existing template, or when draft form type + version + section are chosen. */
  const canSendLayoutAssistantMessage = useMemo(
    () =>
      Boolean(initial?.id) ||
      (studioFormType != null &&
        schemaPickId.trim() !== '' &&
        sectionName.trim() !== ''),
    [initial?.id, studioFormType, schemaPickId, sectionName]
  );

  const copyLayoutJson = useCallback(async () => {
    if (!layoutJsonText.trim()) {
      toast.error('Nothing to copy', 'layout_json is empty.');
      return;
    }
    try {
      await navigator.clipboard.writeText(layoutJsonText);
      toast.success('Copied', 'layout_json copied to clipboard.');
    } catch {
      toast.error('Clipboard failed', 'Could not write to clipboard.');
    }
  }, [layoutJsonText, toast]);

  const copyLlmBundle = useCallback(async () => {
    const blob = buildContextBundleOrNull();
    if (!blob) {
      toast.error('Nothing to copy', 'Load a section schema first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(blob);
      toast.success(
        'Copied',
        'LLM context bundle (schema + DSL only) copied to clipboard.'
      );
    } catch {
      toast.error('Clipboard failed', 'Could not write to clipboard.');
    }
  }, [buildContextBundleOrNull, toast]);

  const sendVibecoderChat = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || chatSending || !canSendLayoutAssistantMessage) return;
    const bundle = buildContextBundleOrNull();
    if (!bundle) {
      toast.error(
        'Missing context',
        'Pick schema version, section, and wait for questions to load.'
      );
      return;
    }
    const nextMessages: VibecoderChatTurn[] = [
      ...chatMessages,
      { role: 'user', content: text },
    ];
    setChatSending(true);
    try {
      const res = await client.post({
        url: '/vibecoder-layout-assistant/chat',
        body: {
          messages: nextMessages,
          context_bundle: bundle,
          current_layout_json_text: layoutJsonText,
        },
        headers: {
          'Content-Type': 'application/json',
        },
        responseType: 'json',
        throwOnError: true,
      });
      const data = res.data as {
        assistant_message: string;
        layout_json?: unknown[] | null;
      };
      const prevLayoutText = layoutJsonText;
      const displayAssistant = sanitizeVibecoderAssistantDisplay(
        data.assistant_message ?? ''
      );
      const appliedLayout =
        data.layout_json != null && Array.isArray(data.layout_json);
      const nextLayoutText = appliedLayout
        ? JSON.stringify(data.layout_json, null, 2)
        : null;

      setChatMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content:
            displayAssistant ||
            (appliedLayout ? 'Updated layout_json in the editor below.' : ''),
        },
      ]);
      setChatInput('');
      if (nextLayoutText != null) {
        setLayoutJsonText(nextLayoutText);
        if (nextLayoutText !== prevLayoutText) {
          toast.success('Layout updated', 'Applied to the layout_json field.');
        }
      }
    } catch (err) {
      let msg = 'Request failed';
      if (isAxiosError(err)) {
        const d = err.response?.data as { detail?: unknown } | undefined;
        const detail = d?.detail;
        if (typeof detail === 'string') msg = detail;
        else msg = err.message;
      }
      toast.error('Assistant failed', msg);
    } finally {
      setChatSending(false);
    }
  }, [
    chatInput,
    chatSending,
    canSendLayoutAssistantMessage,
    buildContextBundleOrNull,
    chatMessages,
    layoutJsonText,
    toast,
  ]);

  function resetDraftFields() {
    const d = emptyDraftState();
    setSchemaPickId(d.schemaPickId);
    setSectionName(d.sectionName);
    setLayoutJsonText(d.layoutJsonText);
    setName(d.name);
    setDescription(d.description);
    setIsEnabled(d.isEnabled);
    setSchemaSectionKeys([]);
    setStudioAnswers({});
    setCoverage(null);
    setCoverageWorking(false);
    schemaSectionFetchGen.current += 1;
    setChatMessages([]);
    setChatInput(DEFAULT_LAYOUT_ASSISTANT_PROMPT);
  }

  const handleSubmit = () => {
    if (!parsedLayout.ok) return;
    if (coverage && coverage.uncovered.length > 0) {
      if (
        !window.confirm(
          `${coverage.uncovered.length} schema question(s) were not reachable in this layout (under the automated search).\nSave anyway?`
        )
      ) {
        return;
      }
    }

    const layout = parsedLayout.layout;

    if (isEditingPublishedJson && initial) {
      onSaveJson({
        layout_json: layout,
        name: name || undefined,
        description: description || undefined,
        is_enabled: isEnabled,
      } satisfies BespokeTemplateUpdate);
      return;
    }

    const pickSave =
      schemaPickId && studioSchemaPicks.find(p => p.id === schemaPickId);

    if (!pickSave || !sectionName.trim() || !name.trim()) return;

    onSaveJson({
      schema_version_name: pickSave.schemaVersion.name,
      section_name: sectionName.trim(),
      layout_json: layout,
      name,
      description: description || undefined,
      is_enabled: isEnabled,
    } satisfies BespokeTemplateCreate);
  };

  const selectedPick =
    schemaPickId.trim() !== ''
      ? studioSchemaPicks.find(p => p.id === schemaPickId)
      : undefined;

  const canSubmitCreate =
    !!selectedPick &&
    !!sectionName.trim() &&
    !!name.trim() &&
    layoutJsonText.trim().length > 0 &&
    parsedLayout.ok;

  const canSubmitEdit =
    isEditingPublishedJson &&
    !!name.trim() &&
    layoutJsonText.trim().length > 0 &&
    parsedLayout.ok;

  const previewSectionName =
    sectionName.trim() || initial?.section_name?.trim() || 'preview';

  return (
    <Card className='border-primary/25 shadow-sm'>
      <CardHeader className='space-y-1 pb-4'>
        <CardTitle className='text-xl'>Template Vibecoder</CardTitle>
        <CardDescription>
          Create bespoke section layouts. Choose form type, schema version, and
          section below to start a new template, or use <strong>Edit</strong> on
          a row in the Bespoke Template Registry above to open an existing one.
        </CardDescription>
      </CardHeader>

      <CardContent className='space-y-5 pt-0'>
        {isEditingPublishedJson && initial && (
          <p className='text-xs text-muted-foreground'>
            Editing <strong className='font-medium'>{initial.name}</strong> —
            registration target cannot change here.
          </p>
        )}

        {!isEditingPublishedJson && (
          <div className='grid gap-4 grid-cols-1 md:grid-cols-3'>
            <div className='space-y-1 min-w-0'>
              <label className='text-sm font-medium'>Form schema type</label>
              <Select
                value={studioFormType ?? ''}
                onValueChange={v => setStudioFormType(v as RogerDocFormType)}
                disabled={catalogLoading || formTypeCatalog.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      catalogLoading ? 'Loading form types…' : 'No form types'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {formTypeCatalog.map(row => (
                    <SelectItem key={row.form_type} value={row.form_type}>
                      {row.display_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-1 min-w-0'>
              <label className='text-sm font-medium'>Schema version</label>
              {catalogLoading || studioVersionsLoading ? (
                <div className='flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm text-muted-foreground'>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin shrink-0' />
                  Loading schemas…
                </div>
              ) : studioSchemaPicks.length === 0 ? (
                <p className='text-xs text-muted-foreground'>
                  No schema versions for this form type.
                </p>
              ) : (
                <Select value={schemaPickId} onValueChange={setSchemaPickId}>
                  <SelectTrigger>
                    <SelectValue placeholder='Choose schema row…' />
                  </SelectTrigger>
                  <SelectContent>
                    {studioSchemaPicks.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className='space-y-1 min-w-0'>
              <label className='text-sm font-medium'>Section</label>
              {schemaSectionsLoading && schemaPickId ? (
                <div className='flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm text-muted-foreground'>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin shrink-0' />
                  Loading sections…
                </div>
              ) : schemaSectionKeys.length > 0 ? (
                <Select value={sectionName} onValueChange={setSectionName}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select section…' />
                  </SelectTrigger>
                  <SelectContent>
                    {schemaSectionKeys.map(s => (
                      <SelectItem key={s} value={s}>
                        {formatSectionDropdownLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : schemaPickId ? (
                <Input
                  value={sectionName}
                  onChange={e => setSectionName(e.target.value)}
                  placeholder='Sections did not load for this schema row'
                />
              ) : (
                <Input
                  value={sectionName}
                  onChange={e => setSectionName(e.target.value)}
                  placeholder='Pick a schema first'
                  disabled
                />
              )}
            </div>
          </div>
        )}

        {isEditingPublishedJson && initial && (
          <div className='grid gap-2 text-sm sm:grid-cols-2'>
            <div>
              <span className='text-muted-foreground'>Schema version</span>
              <p className='font-mono text-xs'>{initial.schema_version_name}</p>
            </div>
            <div>
              <span className='text-muted-foreground'>Section</span>
              <p className='font-medium' title={initial.section_name}>
                {formatSectionDropdownLabel(initial.section_name)}
              </p>
            </div>
          </div>
        )}

        <div className='grid gap-6 xl:grid-cols-3 xl:items-stretch'>
          <div className='flex min-h-0 min-w-0 flex-col gap-2 xl:h-full'>
            <div className='flex shrink-0 flex-wrap items-start justify-between gap-2'>
              <label className='text-sm font-medium pt-0.5'>layout_json</label>
              <div className='flex flex-wrap justify-end gap-1.5'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='h-8 text-xs'
                  onClick={() => void copyLayoutJson()}
                  disabled={!layoutJsonText.trim()}
                >
                  <Clipboard className='h-3.5 w-3.5 mr-1' />
                  Copy layout JSON
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='h-8 text-xs'
                  disabled={sectionQuestions.length === 0 || questionsLoading}
                  onClick={() => void copyLlmBundle()}
                >
                  <Clipboard className='h-3.5 w-3.5 mr-1' />
                  Copy LLM DSL bundle
                </Button>
              </div>
            </div>
            <Textarea
              value={layoutJsonText}
              onChange={e => setLayoutJsonText(e.target.value)}
              placeholder='LayoutNode[] JSON here...'
              rows={6}
              className='font-mono text-[11px] leading-snug min-h-[5rem] max-h-[11rem] shrink-0'
              spellCheck={false}
            />
            {!parsedLayout.ok && layoutJsonText.trim().length > 0 && (
              <p className='text-xs text-destructive shrink-0'>
                {parsedLayout.error}
              </p>
            )}

            <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border-2 border-primary/25 bg-primary/[0.06] shadow-sm ring-1 ring-primary/10'>
              <div className='shrink-0 border-b border-primary/20 bg-primary/10 px-3 py-2.5'>
                <span className='text-sm font-semibold tracking-tight text-foreground'>
                  Layout assistant
                </span>
                <p className='text-[11px] text-muted-foreground mt-1'>
                  Server-side LLM (configurable) with schema + DSL context.
                  Replies can replace{' '}
                  <code className='text-[10px]'>layout_json</code>{' '}
                  automatically.
                </p>
              </div>
              <div className='min-h-0 flex-1 overflow-y-auto p-2 space-y-2'>
                {chatMessages.length === 0 ? (
                  <p className='text-[11px] text-muted-foreground px-1'>
                    Select form type, schema version, and section so the
                    assistant can load schema context (or edit an existing
                    template). You can adjust the draft message below before
                    sending.
                  </p>
                ) : (
                  chatMessages.map((m, i) => (
                    <div
                      key={`${m.role}-${i}`}
                      className={`rounded-md px-2 py-1.5 text-[11px] leading-snug whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-primary/10 ml-4'
                          : 'bg-background mr-4 border'
                      }`}
                    >
                      <span className='font-medium text-[10px] text-muted-foreground block mb-0.5'>
                        {m.role === 'user' ? 'You' : 'Assistant'}
                      </span>
                      {m.content}
                    </div>
                  ))
                )}
              </div>
              <div className='flex shrink-0 gap-2 border-t p-2'>
                <Textarea
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  placeholder='Describe changes…'
                  rows={2}
                  className='min-h-[2.5rem] flex-1 resize-none text-[11px]'
                  disabled={chatSending}
                  onKeyDown={e => {
                    const bundleReady = buildContextBundleOrNull() != null;
                    if (
                      e.key === 'Enter' &&
                      !e.shiftKey &&
                      !chatSending &&
                      canSendLayoutAssistantMessage &&
                      chatInput.trim() &&
                      !questionsLoading &&
                      bundleReady
                    ) {
                      e.preventDefault();
                      void sendVibecoderChat();
                    }
                  }}
                />
                <Button
                  type='button'
                  size='sm'
                  className='shrink-0 self-end'
                  disabled={
                    chatSending ||
                    !chatInput.trim() ||
                    !canSendLayoutAssistantMessage ||
                    questionsLoading ||
                    buildContextBundleOrNull() == null
                  }
                  onClick={() => void sendVibecoderChat()}
                >
                  {chatSending ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <SendHorizonal className='h-4 w-4' />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div className='flex min-h-[36rem] min-w-0 flex-col gap-3 rounded-lg border bg-muted/20 p-4 xl:col-span-2'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-sm font-medium'>Coverage & preview</span>
              {questionsLoading && (
                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
              )}
              {sectionQuestions.length > 0 && parsedLayout.ok && (
                <Badge variant='outline' className='text-xs font-mono'>
                  {coverageWorking
                    ? 'Reachability searching…'
                    : reachableCount != null
                      ? `Reachable ~ ${reachableCount}/${targetN}`
                      : 'Reachability pending'}
                </Badge>
              )}
            </div>
            {questionsLoading ? (
              <p className='text-xs text-muted-foreground'>
                Loading RogerDocs section schema…
              </p>
            ) : sectionQuestions.length === 0 ? (
              <p className='text-xs text-amber-800 dark:text-amber-400'>
                Pick schema + section to load RogerDoc slice for preview.
              </p>
            ) : null}

            {coverage && coverage.uncovered.length > 0 && parsedLayout.ok && (
              <div className='text-xs'>
                <p className='font-medium text-amber-800 dark:text-amber-400'>
                  Unreachable under search ({coverage.uncovered.length})
                </p>
                <pre className='mt-1 max-h-32 overflow-auto rounded border bg-background p-2 text-[11px] leading-tight whitespace-pre-wrap'>
                  {coverage.uncovered.join(', ')}
                </pre>
              </div>
            )}
            {strayLayoutQids.length > 0 && (
              <div className='text-xs text-destructive'>
                Referenced QIDs missing from RogerDocs slice:{' '}
                <span className='font-mono'>{strayLayoutQids.join(', ')}</span>
              </div>
            )}

            <div className='min-h-[18rem] max-h-[min(720px,75vh)] flex-1 overflow-y-auto rounded-md border bg-card p-3'>
              {parsedLayout.ok ? (
                <BespokeLayoutRenderer
                  layout={parsedLayout.layout}
                  answers={studioAnswers}
                  onAnswerChange={(qid, value) =>
                    setStudioAnswers(prev => ({ ...prev, [qid]: value }))
                  }
                  onFormDataChange={() => {}}
                  schema={sectionQuestions}
                  schemaQuestionOptions={[]}
                  sectionName={previewSectionName}
                />
              ) : (
                <p className='text-sm text-muted-foreground'>
                  Fix JSON to render an interactive preview.
                </p>
              )}
            </div>
            {parsedLayout.ok && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='w-full text-xs'
                onClick={() => setStudioAnswers({})}
              >
                Reset preview answers
              </Button>
            )}
          </div>
        </div>

        <div className='grid gap-4 sm:grid-cols-2'>
          <div className='space-y-1'>
            <label className='text-sm font-medium'>Name</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='Human-friendly label'
            />
          </div>
          <div className='space-y-1 flex items-end gap-3 pb-[2px]'>
            <div className='flex items-center gap-3'>
              <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
              <label className='text-sm'>Enabled</label>
            </div>
          </div>
        </div>
        <div className='space-y-1'>
          <label className='text-sm font-medium'>Description</label>
          <Textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder='Optional notes...'
            rows={3}
          />
        </div>

        <div className='flex flex-wrap justify-end gap-2 border-t pt-4'>
          {isEditingPublishedJson ? (
            <Button type='button' variant='outline' onClick={onDiscardEdit}>
              Cancel edit
            </Button>
          ) : (
            <Button type='button' variant='outline' onClick={resetDraftFields}>
              Clear draft
            </Button>
          )}
          <Button
            type='button'
            onClick={handleSubmit}
            disabled={
              saving ||
              (!isEditingPublishedJson && !canSubmitCreate) ||
              (isEditingPublishedJson && !canSubmitEdit)
            }
          >
            {saving && (
              <Loader2 className='h-4 w-4 animate-spin mr-2' aria-hidden />
            )}
            {isEditingPublishedJson ? 'Save changes' : 'Create'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
